// Samordning av flera projektörers underlag till ett gemensamt dokument.
// Dok 30c – Samordnad risksammanställning
// Dok 10b – Samlad arbetsområdesutredning
import { supabase } from "@/integrations/supabase/client";
import { fragorData } from "@/lib/fragorData";
import type { SvarData } from "@/types/riskbedomning";

export type ConsolidationType = "risk" | "work_area";

export interface ConsolidationSource {
  id: string;
  kind: "internal" | "designer" | "survey";
  label: string;
  /** Disciplin eller ansvarig part, t.ex. "K – Konstruktion". */
  discipline?: string;
  company?: string;
  phase?: string;
}

export interface RiskEntry {
  sourceId: string;
  answer?: string;
  risk?: string;
  action?: string;
  level?: string;
}

export interface RiskGroup {
  key: string;
  question: string;
  category: string;
  entries: RiskEntry[];
  /** True när källorna svarat olika (t.ex. ja/nej eller olika risknivå). */
  conflict: boolean;
}

export interface WorkAreaEntry {
  sourceId: string;
  status?: string;
  comment?: string;
  responsible?: string;
}

export interface WorkAreaGroup {
  key: string;
  controlPoint: string;
  category: string;
  legalReference?: string;
  entries: WorkAreaEntry[];
  conflict: boolean;
}

/** Bas-P:s beslut per rad när källorna säger olika saker. */
export interface Resolution {
  include?: boolean;
  decision?: string;
  level?: string;
  motivation?: string;
}

export type Resolutions = Record<string, Resolution>;

export interface ConsolidationResult<TGroup> {
  sources: ConsolidationSource[];
  groups: TGroup[];
  conflicts: number;
}

const RISK_LEVEL_LABELS: Record<string, string> = {
  low: "Låg",
  medium: "Medel",
  high: "Hög",
  critical: "Kritisk",
};

export const riskLevelLabel = (value?: string | null) =>
  value ? RISK_LEVEL_LABELS[value] ?? value : "";

const levelFromMatrix = (svar: SvarData): string | undefined => {
  const s = Number(svar.sannolikhet ?? 0);
  const k = Number(svar.konsekvens ?? 0);
  if (!s || !k) return undefined;
  const score = s * k;
  if (score >= 15) return "critical";
  if (score >= 9) return "high";
  if (score >= 4) return "medium";
  return "low";
};

const questionIndex = new Map(fragorData.map((f) => [f.id, f]));

const normalize = (value?: string | null) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

/* ------------------------------------------------------------------ */
/*  Dok 30c – risker                                                   */
/* ------------------------------------------------------------------ */

export const collectRiskConsolidation = async (
  projectId: string,
  phase: "utforande" | "bruksskede"
): Promise<ConsolidationResult<RiskGroup>> => {
  const [{ data: riskRows }, { data: designerRows }] = await Promise.all([
    supabase
      .from("folder_risk_assessments")
      .select("id, name, assessment_id, phase, data, created_at")
      .eq("project_id", projectId)
      .is("deleted_at", null)
      .limit(500),
    supabase
      .from("designer_submissions")
      .select("id, discipline, company, residual_risks_construction, residual_risks_use")
      .eq("project_id", projectId)
      .limit(200),
  ]);

  const rows = ((riskRows as unknown as Record<string, any>[]) || []).filter((r) =>
    phase === "bruksskede" ? r.phase === "bruksskede" : r.phase !== "bruksskede"
  );

  const sources: ConsolidationSource[] = rows.map((r) => ({
    id: String(r.id),
    kind: "internal",
    label: String(r.name || "Risksammanställning"),
    phase: r.phase ?? undefined,
  }));

  const groupMap = new Map<string, RiskGroup>();

  for (const row of rows) {
    const svarMap = ((row.data as any)?.svar ?? {}) as Record<string, SvarData>;
    for (const [questionId, svar] of Object.entries(svarMap)) {
      if (!svar) continue;
      const answer = normalize(svar.svar);
      const hasContent =
        answer === "ja" ||
        !!svar.identifieradRisk ||
        !!svar.kommentar ||
        !!svar.atgard ||
        !!svar.organisatoriskAtgard ||
        !!svar.tekniskAtgard;
      if (!hasContent) continue;

      const q = questionIndex.get(questionId);
      const text = q?.text || svar.questionText || `Fråga ${questionId}`;
      const key = normalize(text) || questionId;
      const group =
        groupMap.get(key) ??
        ({
          key,
          question: text,
          category: q?.kategori || "Övrigt",
          entries: [],
          conflict: false,
        } as RiskGroup);

      group.entries.push({
        sourceId: String(row.id),
        answer: svar.svar || "",
        risk: svar.identifieradRisk || svar.kommentar || "",
        action:
          [svar.tekniskAtgard, svar.organisatoriskAtgard, svar.atgard]
            .filter(Boolean)
            .join("\n") || "",
        level: (svar as { riskNiva?: string }).riskNiva || levelFromMatrix(svar),
      });
      groupMap.set(key, group);
    }
  }

  // Projektörsinlämningar (Dok 16) som fritext – en rad per disciplin.
  for (const d of ((designerRows as unknown as Record<string, any>[]) || [])) {
    const text =
      phase === "bruksskede" ? d.residual_risks_use : d.residual_risks_construction;
    if (!text || !String(text).trim()) continue;
    const label = `${d.discipline}${d.company ? ` · ${d.company}` : ""}`;
    sources.push({
      id: String(d.id),
      kind: "designer",
      label,
      discipline: d.discipline,
      company: d.company ?? undefined,
    });
    const key = `projektorinlamning:${d.id}`;
    groupMap.set(key, {
      key,
      question: `Kvarvarande risk redovisad av ${label}`,
      category: "Projektörsinlämning (Dok 16)",
      entries: [
        {
          sourceId: String(d.id),
          answer: "ja",
          risk: String(text),
          action: "",
        },
      ],
      conflict: false,
    });
  }

  const groups = Array.from(groupMap.values()).map((g) => ({
    ...g,
    conflict: hasRiskConflict(g),
  }));
  groups.sort((a, b) => a.category.localeCompare(b.category, "sv") || a.question.localeCompare(b.question, "sv"));

  return { sources, groups, conflicts: groups.filter((g) => g.conflict).length };
};

const hasRiskConflict = (group: RiskGroup): boolean => {
  if (group.entries.length < 2) return false;
  const answers = new Set(group.entries.map((e) => normalize(e.answer)).filter(Boolean));
  const levels = new Set(group.entries.map((e) => e.level).filter(Boolean));
  return answers.size > 1 || levels.size > 1;
};

/* ------------------------------------------------------------------ */
/*  Dok 10b – arbetsområdesutredningar                                 */
/* ------------------------------------------------------------------ */

export const collectWorkAreaConsolidation = async (
  projectId: string
): Promise<ConsolidationResult<WorkAreaGroup>> => {
  const { data: surveys } = await supabase
    .from("work_area_surveys")
    .select("id, area_name, status, created_at")
    .eq("project_id", projectId)
    .order("created_at", { ascending: true })
    .limit(200);

  const surveyRows = (surveys as unknown as Record<string, any>[]) || [];
  const sources: ConsolidationSource[] = surveyRows.map((s) => ({
    id: String(s.id),
    kind: "survey",
    label: String(s.area_name || "Arbetsområde"),
  }));

  if (surveyRows.length === 0) return { sources, groups: [], conflicts: 0 };

  const { data: items } = await supabase
    .from("work_area_survey_items")
    .select("id, survey_id, category, control_point, legal_reference, status, comment, responsible, sort_order")
    .in("survey_id", surveyRows.map((s) => String(s.id)))
    .order("sort_order", { ascending: true })
    .limit(5000);

  const groupMap = new Map<string, WorkAreaGroup>();
  for (const it of ((items as unknown as Record<string, any>[]) || [])) {
    const key = normalize(it.control_point) || String(it.id);
    const group =
      groupMap.get(key) ??
      ({
        key,
        controlPoint: String(it.control_point || ""),
        category: String(it.category || "Övrigt"),
        legalReference: it.legal_reference ?? undefined,
        entries: [],
        conflict: false,
      } as WorkAreaGroup);
    group.entries.push({
      sourceId: String(it.survey_id),
      status: it.status ?? undefined,
      comment: it.comment ?? undefined,
      responsible: it.responsible ?? undefined,
    });
    groupMap.set(key, group);
  }

  const groups = Array.from(groupMap.values()).map((g) => ({
    ...g,
    conflict:
      g.entries.length > 1 &&
      new Set(g.entries.map((e) => e.status).filter(Boolean)).size > 1,
  }));
  groups.sort(
    (a, b) =>
      a.category.localeCompare(b.category, "sv") ||
      a.controlPoint.localeCompare(b.controlPoint, "sv")
  );

  return { sources, groups, conflicts: groups.filter((g) => g.conflict).length };
};

export const WORK_AREA_STATUS_LABELS: Record<string, string> = {
  yes: "Ja",
  no: "Nej",
  na: "Ej aktuellt",
};

/** Sammanfattande beslutstext som används i Word-exporten. */
export const resolvedRiskLevel = (group: RiskGroup, resolution?: Resolution): string => {
  if (resolution?.level) return riskLevelLabel(resolution.level);
  const levels = group.entries.map((e) => e.level).filter(Boolean) as string[];
  if (!levels.length) return "";
  const order = ["low", "medium", "high", "critical"];
  const highest = levels.sort((a, b) => order.indexOf(b) - order.indexOf(a))[0];
  return riskLevelLabel(highest);
};

export const isIncluded = (resolutions: Resolutions, key: string): boolean =>
  resolutions[key]?.include !== false;
